import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useStudents } from 'context/StudentsContext';
import { humanizeSupabaseError } from '../../../lib/supabaseErrors';

const gradeOptions = [
  { value: 'pre-k', label: 'Pre-K' },
  { value: 'k', label: 'Kindergarten' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}${
      i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'
    } Grade`,
  })),
];

const disabilityOptions = [
  { value: '', label: 'Not specified' },
  { value: 'autism', label: 'Autism Spectrum Disorder' },
  { value: 'intellectual', label: 'Intellectual Disability' },
  { value: 'learning', label: 'Specific Learning Disability' },
  { value: 'emotional', label: 'Emotional Behavioral Disorder' },
  { value: 'speech', label: 'Speech or Language Impairment' },
  { value: 'hearing', label: 'Hearing Impairment' },
  { value: 'visual', label: 'Visual Impairment' },
  { value: 'physical', label: 'Physical Disability' },
  { value: 'multiple', label: 'Multiple Disabilities' },
  { value: 'other', label: 'Other Health Impairment' },
];

const emptyForm = {
  firstName: '',
  lastName: '',
  grade: '',
  primaryDisability: '',
  iepStartDate: '',
  iepAnnualReviewDue: '',
  notes: '',
};

const AddStudentModal = ({ isOpen, onClose, onCreated }) => {
  const { addStudent, isLive, students } = useStudents();
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showMore, setShowMore] = useState(false);
  // Set to true when the user tries to save and there's a duplicate name;
  // we show a soft warning + require a second click to confirm.
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
  const dialogRef = useRef(null);

  // Stash onClose in a ref so the "reset form on open" effect below can
  // depend on isOpen ALONE. Parents pass onClose as an inline arrow
  // (`onClose={() => setIsAddStudentOpen(false)}`), which gets a fresh
  // reference on every parent render. If we put onClose in the effect's
  // deps, ANY parent re-render — the Dashboard clock ticking, a context
  // update, a sibling state change — would re-fire the effect and wipe
  // every field the teacher had already typed. On mobile this manifested
  // as "Primary disability resets to Not specified when I tap the Notes
  // box, and Grade resets on scroll" — see subagent bug report.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setFormData(emptyForm);
    setErrors({});
    setSubmitError(null);
    setShowMore(false);
    setDuplicateConfirmed(false);

    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    // Focus the first field for keyboard-only users.
    const t = setTimeout(() => {
      const el = dialogRef.current?.querySelector('input, select, textarea');
      el?.focus?.();
    }, 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    // A change to first/last name invalidates any previous duplicate confirmation.
    if (field === 'firstName' || field === 'lastName') {
      setDuplicateConfirmed(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.firstName.trim()) next.firstName = 'First name is required';
    if (!formData.lastName.trim()) next.lastName = 'Last name is required';
    if (!formData.grade) next.grade = 'Grade is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Is there already a student with the same first + last name (case-insensitive,
  // ignoring surrounding whitespace)? Used to soft-warn on duplicates so a
  // teacher doesn't accidentally create Joe Jones twice.
  const duplicateExists = (() => {
    const f = formData.firstName.trim().toLowerCase();
    const l = formData.lastName.trim().toLowerCase();
    if (!f || !l) return false;
    return (students || []).some(
      (s) =>
        (s.firstName || '').trim().toLowerCase() === f &&
        (s.lastName  || '').trim().toLowerCase() === l,
    );
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // If a student with the same name already exists AND the user hasn't
    // explicitly confirmed the duplicate, bail out and surface the
    // yellow warning banner (see JSX below).
    if (duplicateExists && !duplicateConfirmed) {
      setDuplicateConfirmed(false);
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    try {
      const created = await addStudent({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        grade: formData.grade,
        primaryDisability: formData.primaryDisability || null,
        iepStartDate: formData.iepStartDate || null,
        iepAnnualReviewDue: formData.iepAnnualReviewDue || null,
        notes: formData.notes.trim() || null,
      });
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setSubmitError(
        humanizeSupabaseError(
          err,
          'Could not save student. Please check your connection and try again.',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Single-scroll-container pattern:
  //
  //   outer <div fixed inset-0 overflow-y-auto>   ← ONLY thing that scrolls
  //     <div flex items-start p-4 min-h-full>     ← centers when short
  //       <div (the modal card)>                  ← no overflow, no max-h
  //         sticky header + form body
  //
  // The previous version had TWO overflow-y-auto containers (outer wrapper
  // and modal card). iOS Safari has a well-documented bug where nested
  // scroll containers can lock the outer scroll — the user sees the
  // modal, but swiping down at the top of the modal only scrolls the
  // *inner* container, which is already at scrollTop=0, so nothing
  // happens and they can't reach the fields above. Collapsing to a
  // single scroll container fixes it: the whole outer div scrolls, and
  // the modal card grows naturally.
  //
  // items-start on mobile so a tall form starts at the top (scroll down
  // to reach Save). sm:items-center on larger screens so a short form
  // vertically centers nicely.
  //
  // min-h-full on the inner flex row is what makes items-center work in
  // a scrolling container — without it, "center" is calculated against
  // the content height, not the viewport, and short modals hug the top.
  // A click on the overlay OR the padding around the card dismisses the
  // modal. `e.target === e.currentTarget` filters out clicks that
  // originate inside the card (they'd bubble through with a different
  // target). We check on both the outer overlay and the inner flex row
  // so the "click outside to dismiss" affordance works whether the
  // click lands on the black overlay or on the padding around the card.
  const overlayDismiss = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/40 overflow-y-auto overscroll-contain"
      onClick={overlayDismiss}
    >
      <div
        className="min-h-full flex items-start sm:items-center justify-center p-4"
        onClick={overlayDismiss}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-student-title"
          className="w-full max-w-lg rounded-xl bg-white shadow-xl my-4 sm:my-8"
        >
        <div className="flex items-start justify-between border-b border-gray-200 p-5 sticky top-0 bg-white z-10 rounded-t-xl">
          <div>
            <h2
              id="add-student-title"
              className="text-lg font-semibold text-gray-900"
            >
              Add a student
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Just their name and grade to start. You can fill in the
              rest — IEP dates, accommodations, contacts — from their
              profile page later.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* noValidate for the same reason as LoginForm: the modal renders
            its own red-text errors from `errors[]`, so we don't want the
            browser's native "Please fill out this field" tooltip to
            intercept before our onSubmit handler runs. */}
        <form onSubmit={handleSubmit} className="space-y-4 p-5" noValidate>
          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 whitespace-pre-line">
              {submitError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e?.target?.value)}
              error={errors.firstName}
              required
            />
            <Input
              label="Last name"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e?.target?.value)}
              error={errors.lastName}
              required
            />
          </div>

          <Select
            label="Grade"
            placeholder="Select grade"
            options={gradeOptions}
            value={formData.grade}
            onChange={(value) => handleChange('grade', value)}
            error={errors.grade}
            required
          />

          {/* Soft duplicate-name warning. Fires only after the teacher
              has typed both first + last and a matching student is
              already on the roster. Requires a second click to confirm
              — this prevents the "Joe Jones × 2" bug from the
              usability walkthrough. */}
          {duplicateExists && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="mt-0.5 flex-shrink-0 text-amber-700" />
                <div className="flex-1">
                  <p className="font-semibold">
                    Heads up — there&apos;s already a student named
                    &ldquo;{formData.firstName.trim()} {formData.lastName.trim()}&rdquo; on your roster.
                  </p>
                  <p className="mt-1">
                    If this is a different student with the same name,
                    click <span className="font-semibold">Save student</span> again to add them anyway.
                    Otherwise, click Cancel.
                  </p>
                  {duplicateConfirmed && (
                    <p className="mt-1 text-xs italic">
                      Adding as a new student on the next click.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Progressive disclosure: optional fields (disability, IEP
              dates, notes) are hidden by default so the first-time
              view has only 3 fields (first/last name + grade). A
              teacher can flip "Add more info" open if they have the
              IEP paperwork in front of them. */}
          <div>
            <button
              type="button"
              onClick={() => setShowMore((v) => !v)}
              aria-expanded={showMore}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-800 transition-colors text-sm font-medium"
            >
              <Icon name={showMore ? 'ChevronUp' : 'Plus'} size={16} />
              {showMore
                ? 'Hide extra info'
                : 'Add more info (optional — disability, IEP dates, notes)'}
            </button>
          </div>

          {showMore && (
            <div className="space-y-4 rounded-lg border border-dashed border-gray-200 bg-gray-50/40 p-4">
              <Select
                label="Primary disability (optional)"
                placeholder="Not specified"
                options={disabilityOptions}
                value={formData.primaryDisability}
                onChange={(value) => handleChange('primaryDisability', value)}
              />

              {/* IEP dates. Optional at student-creation time: a teacher
                  can add a student to the roster before the IEP paperwork
                  lands and fill these in from the profile page later.
                  When the start date is filled but review date isn't,
                  we suggest start + 1 year to match the federal
                  annual-review cadence. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="IEP start date (optional)"
                  type="date"
                  value={formData.iepStartDate}
                  onChange={(e) => {
                    const start = e?.target?.value || '';
                    handleChange('iepStartDate', start);
                    if (start && !formData.iepAnnualReviewDue) {
                      const d = new Date(start);
                      if (!Number.isNaN(d.getTime())) {
                        d.setFullYear(d.getFullYear() + 1);
                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        handleChange('iepAnnualReviewDue', `${yyyy}-${mm}-${dd}`);
                      }
                    }
                  }}
                />
                <Input
                  label="Annual review due (optional)"
                  type="date"
                  value={formData.iepAnnualReviewDue}
                  onChange={(e) =>
                    handleChange('iepAnnualReviewDue', e?.target?.value || '')
                  }
                  description="Federal 12-month deadline. Auto-fills to +1 year when you set a start date."
                />
              </div>

              <div>
                <label
                  htmlFor="add-student-notes"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Notes (optional)
                </label>
                <textarea
                  id="add-student-notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e?.target?.value)}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder="Anything a colleague filling in for you should know at a glance (e.g. 'sits near door, benefits from movement breaks')"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={duplicateExists && !duplicateConfirmed ? 'warning' : 'default'}
              loading={isSaving}
              iconName={isSaving ? 'Loader2' : 'Save'}
              iconPosition="left"
              onClick={() => {
                // First click on a duplicate arms the confirmation state; the
                // handleSubmit early-return then keeps the modal open with the
                // yellow warning showing. Second click actually saves.
                if (duplicateExists && !duplicateConfirmed) {
                  setDuplicateConfirmed(true);
                }
              }}
            >
              {isSaving
                ? 'Saving…'
                : duplicateExists && !duplicateConfirmed
                  ? 'Save student (name already exists)'
                  : 'Save student'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default AddStudentModal;
